<template>
  <!--
    [2026-10-05] 外设服务(8765)不可达 · 全局排查引导弹窗 (全局单例, 挂 MainLayout)。
    触发链: scaleClient / jinglunReader / go2rtc / networkDiagnose / peripheralDebugApi
    共 7 处报错点 → peripheralAccessHint() → openPeripheralAccessHelper() → 本组件。
    为什么是弹窗不是红条 toast: 修复指引里的 edge:// 设置页地址一长串, 红条里用户
    既看不见也复制不了; 而浏览器安全策略禁止网页直接跳转 edge:// / chrome://
    (2026-10-05 实测 window.open 返 null / a 标签点击无效 / location 赋值静默失败),
    所以给"一键复制 + 粘贴到地址栏"的引导。打开时自动跑 diagnosePeripheralAccess
    体检区分"服务没启动"和"浏览器拦权限", 修完可「重新体检」确认。
  -->
  <a-modal
    :visible="visible"
    title="外设服务不可达 · 排查指引"
    width="640px"
    :footer="null"
    :maskClosable="true"
    @cancel="close"
  >
    <!-- ① 体检结果 -->
    <div v-if="checking" class="pah-loading">
      <a-spin size="small" />
      <span>正在体检：探测 127.0.0.1:8765 与浏览器「本地网络」权限…</span>
    </div>

    <template v-else-if="check">
      <a-alert :type="alertType" show-icon :message="check.title" class="pah-alert" />
      <ul class="pah-lines">
        <li v-for="(l, i) in check.lines" :key="i">{{ l }}</li>
      </ul>

      <!-- ② 设置页地址 (浏览器拦权限场景): 不能直接跳转, 一键复制让用户粘到地址栏 -->
      <div v-if="needSettings" class="pah-settings">
        <div class="pah-settings-title">
          浏览器安全策略限制，<b>网页无法直接打开</b>设置页 —— 点「复制地址」，粘贴到浏览器<b>地址栏</b>打开：
        </div>
        <div class="pah-url-row">
          <a-input :value="check.settingsUrl" readonly class="pah-url" @focus="selectUrl" />
          <a-button type="primary" icon="copy" @click="copyUrl">{{ copied ? '已复制 ✓' : '复制地址' }}</a-button>
        </div>
        <div class="pah-tip">
          打开后把「本地网络」「设备上的应用」都改成「允许」，回本页刷新（F5）。
          也可以点浏览器地址栏左侧的锁 / 调节图标 →「网站设置」，改同样这两项，效果一样。
        </div>
      </div>

      <!-- ③ 修好了 -->
      <div v-if="check.verdict === 'ok'" class="pah-ok">
        权限已恢复 ✓ 请刷新页面（F5）后重试刚才的操作。
      </div>
    </template>

    <div class="pah-footer">
      <a-button icon="reload" :loading="checking" @click="runCheck">重新体检</a-button>
      <a-button @click="close">关闭</a-button>
    </div>
  </a-modal>
</template>

<script>
import {
  diagnosePeripheralAccess,
  registerPeripheralHelperOpener
} from '@/utils/peripheralHealth'

export default {
  name: 'PeripheralAccessHelperModal',
  data() {
    return {
      visible: false,
      checking: false,
      // diagnosePeripheralAccess 的返回值: { serviceReachable, permission, verdict, title, lines, settingsUrl }
      check: null,
      copied: false
    }
  },
  computed: {
    alertType() {
      if (!this.check) return 'info'
      // ok 绿 / 服务没起 橙 / 浏览器拦权限 红 / 原因不明 橙
      return { ok: 'success', service_down: 'warning', browser_blocked: 'error', ambiguous: 'warning' }[this.check.verdict] || 'info'
    },
    needSettings() {
      return !!this.check && (this.check.verdict === 'browser_blocked' || this.check.verdict === 'ambiguous')
    },
    // [2026-10-15] 全局探活状态（MainLayout 的 startPeripheralServiceWatch 写入）
    serviceOnline() {
      return this.$store && this.$store.state.deviceStatus ? this.$store.state.deviceStatus.serviceOnline : null
    }
  },
  watch: {
    // [2026-10-15] 服务复活（登录竞速窗口过去/用户按指引启动了好粮宝）→ 体检确认 + 1.5s 后自动收起。
    // 修复"服务早就好了、弹窗还挂在屏幕上等手动关"的观感（海棠留香案：功能全正常弹窗不消失）。
    serviceOnline(online) {
      if (online === true && this.visible) {
        this.runCheck()
        clearTimeout(this._recoverCloseTimer)
        this._recoverCloseTimer = setTimeout(() => { this.close() }, 1500)
      }
    }
  },
  created() {
    // 挂载即接管全局触发口 (openPeripheralAccessHelper 里有 30s 节流 + 3s 复探防误弹)
    registerPeripheralHelperOpener(this.open)
  },
  beforeDestroy() {
    clearTimeout(this._recoverCloseTimer)
    registerPeripheralHelperOpener(null)
  },
  methods: {
    open() {
      this.visible = true
      this.copied = false
      this.runCheck()
    },
    close() {
      this.visible = false
    },
    async runCheck() {
      this.checking = true
      this.check = null
      this.copied = false
      try {
        this.check = await diagnosePeripheralAccess()
      } catch (e) {
        // diagnosePeripheralAccess 设计上永不抛; 真抛了也别把弹窗卡在转圈
        this.check = null
      }
      this.checking = false
    },
    selectUrl(ev) {
      // readonly 输入框点一下全选, 手动复制兜底也方便
      try { ev.target.select() } catch (e) { /* ignore */ }
    },
    async copyUrl() {
      const url = this.check && this.check.settingsUrl
      if (!url) return
      let ok = false
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url)
          ok = true
        }
      } catch (e) { /* 走兜底 */ }
      if (!ok) {
        try {
          const ta = document.createElement('textarea')
          ta.value = url
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          ok = document.execCommand('copy')
          document.body.removeChild(ta)
        } catch (e) { /* ignore */ }
      }
      this.copied = ok
      if (ok) {
        this.$message.success('已复制，请粘贴到浏览器地址栏打开', 3)
      } else {
        this.$message.warning('自动复制失败，请点击上方地址框手动复制', 4)
      }
    }
  }
}
</script>

<style scoped>
.pah-loading { display: flex; align-items: center; gap: 8px; color: #1890ff; padding: 8px 0; }
.pah-alert { margin-bottom: 10px; }
.pah-lines { margin: 0 0 10px; padding-left: 20px; }
.pah-lines li { margin: 4px 0; line-height: 1.7; color: #333; }
.pah-settings { border: 1px solid #d6e9ff; background: #f0f7ff; border-radius: 6px; padding: 10px 12px; margin-bottom: 10px; }
.pah-settings-title { margin-bottom: 8px; color: #1c5aa0; line-height: 1.6; }
.pah-url-row { display: flex; gap: 8px; align-items: center; }
.pah-url { flex: 1; font-family: monospace; font-size: 12px; }
.pah-tip { margin-top: 8px; font-size: 12px; color: #888; line-height: 1.7; }
.pah-ok { border: 1px solid #b7eb8f; background: #f6ffed; color: #237804; border-radius: 6px; padding: 10px 12px; margin-bottom: 10px; }
.pah-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
</style>
